const COMMON_FRACTIONS = [
  { value: 1 / 8, text: '1/8' },
  { value: 1 / 4, text: '1/4' },
  { value: 1 / 3, text: '1/3' },
  { value: 1 / 2, text: '1/2' },
  { value: 2 / 3, text: '2/3' },
  { value: 3 / 4, text: '3/4' },
] as const;

const FRACTION_TOLERANCE = 1e-6;

/**
 * Format a stored ingredient quantity for display. Decimal values that are very close to
 * common cooking fractions (1/8, 1/4, 1/3, 1/2, 2/3, 3/4) are shown as fractions instead.
 */
export function formatIngredientQuantity(quantity: string): string {
  const trimmed = quantity.trim();
  if (trimmed === '') return '';

  const timesMatch = /^(.+?)\s+x\s+(.+)$/i.exec(trimmed);
  if (timesMatch != null) {
    return `${formatQuantityToken(timesMatch[1]!.trim())} x ${formatQuantityToken(timesMatch[2]!.trim())}`;
  }

  if (!trimmed.includes('/') && trimmed.includes('-')) {
    const dash = trimmed.indexOf('-');
    const left = trimmed.slice(0, dash).trim();
    const right = trimmed.slice(dash + 1).trim();
    if (left !== '' && right !== '') {
      return `${formatQuantityToken(left)}-${formatQuantityToken(right)}`;
    }
  }

  return formatQuantityToken(trimmed);
}

/** Join a formatted quantity with its unit for recipe display. */
export function formatIngredientAmount(quantity: string, unit: string): string {
  return [formatIngredientQuantity(quantity), unit].filter(Boolean).join(' ');
}

/** The multipliers offered when scaling a recipe for display. */
export const RECIPE_SCALE_FACTORS = [1, 2, 3, 4] as const;

/**
 * Multiply a stored ingredient quantity by `factor` for display. Ranges scale both ends and
 * pack sizes ("2 x 400") scale only the pack count. Returns null when there is no number to
 * scale, such as an empty quantity or "to taste", so the caller can show it unchanged.
 */
export function scaleIngredientQuantity(quantity: string, factor: number): string | null {
  const trimmed = quantity.trim();
  if (trimmed === '') return null;

  const timesMatch = /^(.+?)\s+x\s+(.+)$/i.exec(trimmed);
  if (timesMatch != null) {
    const count = scaleQuantityToken(timesMatch[1]!, factor);
    if (count == null) return null;
    return `${count} x ${formatQuantityToken(timesMatch[2]!.trim())}`;
  }

  const rangeMatch = /^([^-]+)-([^-]+)$/.exec(trimmed);
  if (rangeMatch != null) {
    const low = scaleQuantityToken(rangeMatch[1]!, factor);
    const high = scaleQuantityToken(rangeMatch[2]!, factor);
    if (low == null || high == null) return null;
    return `${low}-${high}`;
  }

  return scaleQuantityToken(trimmed, factor);
}

/**
 * Scale an ingredient's quantity and join it with its unit. `scaled` is false when the factor
 * is 1 or the quantity holds no number, which is when the amount reads exactly as saved.
 */
export function scaleIngredientAmount(
  quantity: string,
  unit: string,
  factor: number,
): { text: string; scaled: boolean } {
  const scaled = factor === 1 ? null : scaleIngredientQuantity(quantity, factor);
  if (scaled == null) return { text: formatIngredientAmount(quantity, unit), scaled: false };
  return { text: [scaled, unit].filter(Boolean).join(' '), scaled: true };
}

/**
 * Scale the first number or range in a free-text servings value ("8", "4-6", "Serves 4").
 * Returns null when it holds no number to scale.
 */
export function scaleServings(servings: string, factor: number): string | null {
  const match = /(\d+(?:\.\d+)?(?:\s+\d+\/\d+)?|\d+\/\d+)(?:(\s*[-–]\s*)(\d+(?:\.\d+)?))?/.exec(
    servings,
  );
  if (match == null) return null;
  const low = scaleQuantityToken(match[1]!, factor);
  const high = match[3] == null ? '' : scaleQuantityToken(match[3], factor);
  if (low == null || high == null) return null;
  const replacement = match[3] == null ? low : `${low}${match[2]}${high}`;
  return (
    servings.slice(0, match.index) +
    replacement +
    servings.slice(match.index + match[0].length)
  ).trim();
}

/** Read a servings value as a phrase: a bare "8" or "4-6" gains "servings", "Serves 4" stays. */
export function formatServingsLabel(servings: string): string {
  const trimmed = servings.trim();
  return /^[\d\s./–-]+$/.test(trimmed) ? `${trimmed} servings` : trimmed;
}

/** Label for a scale factor, e.g. "2×". */
export function formatScaleFactor(factor: number): string {
  return `${factor}\u00d7`;
}

function scaleQuantityToken(token: string, factor: number): string | null {
  const value = parseQuantityToken(token);
  if (value == null) return null;
  const result = value * factor;
  return formatDecimalAsFraction(result, String(Math.round(result * 100) / 100));
}

/** Read "2", "1.5", "3/4" or "1 1/2" as a number, or null for anything else. */
function parseQuantityToken(token: string): number | null {
  const trimmed = token.trim();
  const mixed = /^(\d+)\s+(\d+)\s*\/\s*(\d+)$/.exec(trimmed);
  if (mixed != null) {
    const denominator = Number(mixed[3]);
    if (denominator === 0) return null;
    return Number(mixed[1]) + Number(mixed[2]) / denominator;
  }
  const fraction = /^(\d+)\s*\/\s*(\d+)$/.exec(trimmed);
  if (fraction != null) {
    const denominator = Number(fraction[2]);
    if (denominator === 0) return null;
    return Number(fraction[1]) / denominator;
  }
  if (!/^\d*\.?\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

function formatQuantityToken(token: string): string {
  const trimmed = token.trim();
  if (trimmed === '') return '';

  if (/\d\s*\/\s*\d/.test(trimmed)) {
    return trimmed.replace(/\s+/g, ' ');
  }

  const value = Number(trimmed);
  if (!Number.isFinite(value)) return trimmed;

  return formatDecimalAsFraction(value, trimmed);
}

function formatDecimalAsFraction(value: number, fallback: string): string {
  const sign = value < 0 ? '-' : '';
  const absolute = Math.abs(value);
  const whole = Math.floor(absolute + FRACTION_TOLERANCE);
  const fractional = absolute - whole;

  if (fractional < FRACTION_TOLERANCE) {
    return sign + String(whole);
  }

  for (const { value: fractionValue, text } of COMMON_FRACTIONS) {
    if (Math.abs(fractional - fractionValue) < FRACTION_TOLERANCE) {
      if (whole === 0) return sign + text;
      return `${sign}${whole} ${text}`;
    }
  }

  return fallback;
}
